import React, { useRef, useState } from "react";
import styles from "./styles.module.scss";
import { useTranslation } from "react-i18next";
import { UserModel } from "app/models";
import UserAvatar from "app/components/UserAvatar";
import ManagedByNote from "app/components/ManagedByNote";
import { cls } from "app/utils";
import EditContactModal from "../EditContactModal";
import ScheduleModal from "app/components/SpaceTabsContainer/ScheduleModal";
import { schedulingFetchGet } from "app/utils/request/scheduling/fetch";
import DropdownButton from "app/components/DropdownButton";
import { formatPhoneNumber } from "../../utils";

interface ISingleContact {
  user: UserModel;
  handleDelete: (userId: string) => void;
  loading: boolean;
  facilityId: string;
  onUserUpdate: (updatedContact: Partial<UserModel>) => void;
}

const SingleContact = ({
  user,
  handleDelete,
  loading,
  facilityId,
  onUserUpdate,
}: ISingleContact) => {
  const { t } = useTranslation();
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [scheduleModal, setScheduleModal] = useState(false);
  const [isUSGAIntegrationActive, setIntegration] = useState<boolean>(false);

  const blockWrapperClickRef = useRef<boolean>(false);

  const onViewClick = () => {
    fetchAthleteIntegration();
    setScheduleModal((prev) => !prev);
  };

  const handleWrapperClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const isMobile = window.innerWidth <= 768;

    if (!isMobile) {
      return;
    }

    if (blockWrapperClickRef.current) {
      blockWrapperClickRef.current = false;
      return;
    }

    if (scheduleModal) {
      return;
    }

    const target = e.target as HTMLElement;

    if (target.closest("a") || target.closest("button")) {
      return;
    }

    if (
      target.closest(".modal") ||
      target.closest('[class*="modal"]') ||
      target.closest('[class*="Modal"]')
    ) {
      return;
    }

    if (user?.externalId) {
      onViewClick();
    }
  };

  const editModalHandler = () => {
    if (isEditModalOpen) {
      blockWrapperClickRef.current = true;
    }
    setIsEditModalOpen((prev) => !prev);
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    handleDelete(user.id);
  };

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    editModalHandler();
  };

  const fetchAthleteIntegration = async () => {
    try {
      const response = await schedulingFetchGet(`/student_profiles/${user.externalId}/integrations`);

      const integration = response.find(
        (integration: { name: string; }) => integration.name === "ghin",
      );

      setIntegration(integration.status === "active");
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.contact_info_side} onClick={handleWrapperClick}>
        <div className={styles.contact_info_side_wrapper}>
          <UserAvatar
            sizeHeight={40}
            sizeWidth={40}
            srcName={user?.profilePicture || user?.avatarUrl}
            userName={`${user.firstName || ''} ${user.lastName || ''}`}
            className={styles.contact_avatar}
          />
          <div className={styles.user_info_wrapper}>
            {(user?.firstName?.trim() || user?.lastName?.trim()) ? (
              <p
                className={styles.username}
              >{`${user?.firstName} ${user?.lastName}`}</p>
            ) : t('No Name')}
            {user?.email ? (
              <a href={`mailto:${user?.email}`}>
                <p className={styles.email}>
                  {user?.email}
                </p>
              </a>
            ) : <p className={cls(styles.email, styles.no_click)}>{t("No Email")}</p>}
            {user?.normalizedPhone && (
              <a href={`tel:${user?.normalizedPhone}`}>
                <p className={styles.phone_number_mobile}>{formatPhoneNumber(user?.normalizedPhone)}</p>
              </a>
            )}
            <ManagedByNote user={user} className={styles.email} />
          </div>
        </div>
        {user?.normalizedPhone ? (
          <a href={`tel:${user?.normalizedPhone}`}>
            <p className={styles.phone_number}>{formatPhoneNumber(user?.normalizedPhone)}</p>
          </a>
        ) : (
          <p className={cls(styles.phone_number, styles.no_phone)}>
            {t("No Phone")}
          </p>
        )}
      </div>

      <div className={styles.right_side}>
        {user?.externalId && (
          <button className={styles.view_button} onClick={onViewClick}>
            {t("View")}
          </button>
        )}

        <DropdownButton
          btnContent={<i className="bullets" />}
          closeOnClick
        >
          <div className={styles.action_menu}>
            <button
              onClick={handleEditClick}
              className={cls(styles.button, styles.edit)}
              disabled={loading}
            >
              <i className="ico ico-edit-outline" />
              <div style={{ marginLeft: "10px" }}>{t("Edit")}</div>
            </button>
            <button
              onClick={handleDeleteClick}
              className={cls(styles.button, styles.delete)}
              disabled={loading}
            >
              <i className="ico ico-trash-x" />
              <div style={{ marginLeft: "10px" }}>{t("Delete")}</div>
            </button>
          </div>
        </DropdownButton>
      </div>

      {scheduleModal && (
        <ScheduleModal
          modalHandler={onViewClick}
          athleteData={user}
          isModalOpen={scheduleModal}
          tabType={"bookings"}
          isUSGAIntegrationActive={isUSGAIntegrationActive}
        />
      )}

      {isEditModalOpen && (
        <EditContactModal
          isOpen={isEditModalOpen}
          onClose={editModalHandler}
          contact={user}
          facilityId={facilityId}
          onSuccess={onUserUpdate}
        />
      )}
    </div>
  );
};

export default SingleContact;
